import React, { useEffect, useRef, useState } from 'react';
import './follow-up-sheet.scss';
import { useSelector } from 'react-redux';
import { get, post } from '../../../utils/fetchAPI';
import UniversalLoaderForComponent from '../../loader/universalLoaderForComponent';
import FollowUpTable from './follow-up-table';
import HistoryModal from './history-modal';
import { calculateAge, getInitialDate, generatePrintFilename } from './utils';
import { handlePrint as printHistory } from './print-utility';

const FollowUpSheet = ({ patientId, visitDetails, patientDetails, updateState }) => {
    const user = useSelector((state) => state.auth.user);

    const [selectedDate, setSelectedDate] = useState(getInitialDate(visitDetails));
    const [followupTests, setFollowupTests] = useState([]);
    const [testResults, setTestResults] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [patientName, setPatientName] = useState('');
    const [patientAge, setPatientAge] = useState('');
    const [notes, setNotes] = useState('');
    const [reportData, setReportData] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyReports, setHistoryReports] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [allTestsMap, setAllTestsMap] = useState({});
    const [visitFilter, setVisitFilter] = useState(7);

    // Get doctor ID from visitDetails or user
    const doctorId = visitDetails?.doctor || user?.user?._id;

    const handleSaveRef = useRef(null);

    useEffect(() => {
        if (doctorId && selectedDate && patientId) {
            fetchFollowupReport();
        }
    }, [doctorId, selectedDate, patientId]);

    useEffect(() => {
        if (visitDetails?.appointmentDate) {
            const dateStr = visitDetails.appointmentDate;
            const formattedDate = typeof dateStr === 'string'
                ? dateStr.slice(0, 10)
                : new Date(dateStr).toISOString().split('T')[0];
            setSelectedDate(formattedDate);
        }
    }, [visitDetails?.appointmentDate]);

    useEffect(() => {
        if (patientDetails?.name) {
            setPatientName(patientDetails.name);
        }
        if (patientDetails?.dob) {
            const age = calculateAge(patientDetails.dob);
            setPatientAge(age);
        }
    }, [patientDetails]);

    const fetchFollowupReport = async () => {
        if (!patientId || !doctorId) return;

        setIsLoading(true);
        try {
            const res = await get(`/followup-report/${patientId}/${selectedDate}/${doctorId}`);
            if (res.status === 200) {
                setReportData(res.data);

                if (res.data.patientName) setPatientName(res.data.patientName);
                if (res.data.patientAge) setPatientAge(res.data.patientAge);
                if (res.data.notes) setNotes(res.data.notes);

                // Process test results into a map for easy access
                const results = {};
                res.data.tests?.forEach(test => {
                    if (test.hasData) {
                        results[test.followupTest] = {
                            value: test.value || '',
                            breakdownResults: {}
                        };
                        if (test.breakdownResults && test.breakdownResults.length > 0) {
                            test.breakdownResults.forEach(br => {
                                if (br.value) {
                                    results[test.followupTest].breakdownResults[br.name] = br.value;
                                }
                            });
                        }
                    }
                });
                setTestResults(results);
                setFollowupTests(res.data.tests || []);
            }
        } catch (error) {
            console.error('Error fetching follow-up report:', error);
            setFollowupTests([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTestValueChange = (testId, value) => {
        setTestResults(prev => ({
            ...prev,
            [testId]: {
                ...prev[testId],
                value: value
            }
        }));
    };

    const handleBreakdownValueChange = (testId, breakdownName, value) => {
        setTestResults(prev => {
            const current = prev[testId] || { value: '', breakdownResults: {} };
            return {
                ...prev,
                [testId]: {
                    ...current,
                    breakdownResults: {
                        ...current.breakdownResults,
                        [breakdownName]: value
                    }
                }
            };
        });
    };

    const getBreakdownValue = (testId, breakdownName) => {
        const testResult = testResults[testId];
        if (!testResult || !testResult.breakdownResults) return '';
        return testResult.breakdownResults[breakdownName] || '';
    };

    const getTestValue = (testId) => {
        return testResults[testId]?.value || '';
    };

    const handleSave = async () => {
        if (!patientId || !doctorId) return;

        setIsSaving(true);
        try {
            const processedTestResults = followupTests
                .filter(test => {
                    const result = testResults[test.followupTest];
                    if (!result) return false;

                    const hasMainValue = result.value && result.value.trim() !== '';
                    const hasBreakdownValues = result.breakdownResults &&
                        Object.values(result.breakdownResults).some(val => val && val.trim() !== '');

                    return hasMainValue || hasBreakdownValues;
                })
                .map(test => {
                    const result = testResults[test.followupTest];
                    const breakdownResultsArray = result?.breakdownResults ?
                        Object.entries(result.breakdownResults)
                            .filter(([name, value]) => value && value.trim() !== '')
                            .map(([name, value]) => ({ name, value: value.trim() }))
                        : [];

                    return {
                        followupTest: test.followupTest,
                        testName: test.testName,
                        value: result?.value?.trim() || null,
                        breakdownResults: breakdownResultsArray
                    };
                });

            const payload = {
                patientId: patientId,
                date: selectedDate,
                patientName: patientName,
                patientAge: patientAge,
                notes: notes,
                testResults: processedTestResults
            };

            const res = await post('/followup-report/save', payload);
            if (res.status === 200) {
                fetchFollowupReport();
            }
        } catch (error) {
            console.error('Error saving follow-up report:', error);
        } finally {
            setIsSaving(false);
        }
    };

    handleSaveRef.current = handleSave;

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
                e.preventDefault();
                handleSaveRef.current();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const buildTestsMap = (reports) => {
        const testsMap = {};
        const testsWithAnyValue = new Set();

        // First, find all tests that have any value in any visit (main or breakdown)
        reports.forEach((report) => {
            (report.testResults || []).forEach((testResult) => {
                const testIdRaw =
                    typeof testResult.followupTest === 'object' && testResult.followupTest?._id
                        ? testResult.followupTest._id
                        : testResult.followupTest;
                if (!testIdRaw) return;
                const testId = testIdRaw.toString();

                const hasMainValue = testResult.value && String(testResult.value).trim() !== '';
                const hasBreakdownValue =
                    (testResult.breakdownResults || []).some(
                        (br) => br.value && String(br.value).trim() !== ''
                    );

                if (hasMainValue || hasBreakdownValue) {
                    testsWithAnyValue.add(testId);
                }
            });
        });

        // Then, from current followupTests list include only tests that ever had a value
        followupTests.forEach((test) => {
            const testIdRaw = test.followupTest;
            if (!testIdRaw) return;
            const testId = testIdRaw.toString();
            if (!testsWithAnyValue.has(testId)) return;

            if (!testsMap[testId]) {
                testsMap[testId] = {
                    _id: testId,
                    name: test.testName,
                    breakdowns: test.breakdowns || [],
                };
            }
        });

        // Also include any tests that appear only in history (older/removed tests) but had a value
        reports.forEach((report) => {
            (report.testResults || []).forEach((testResult) => {
                const testIdRaw =
                    typeof testResult.followupTest === 'object' && testResult.followupTest?._id
                        ? testResult.followupTest._id
                        : testResult.followupTest;
                if (!testIdRaw) return;
                const testId = testIdRaw.toString();

                if (!testsWithAnyValue.has(testId)) return;
                if (testsMap[testId]) return;

                const testDetails = testResult.followupTestDetails;
                testsMap[testId] = {
                    _id: testId,
                    name: testResult.testName || testDetails?.name || 'Unknown Test',
                    breakdowns:
                        testDetails?.breakdowns ||
                        (typeof testResult.followupTest === 'object'
                            ? testResult.followupTest?.breakdowns
                            : []) ||
                        [],
                };
            });
        });

        return testsMap;
    };

    const fetchHistory = async () => {
        if (!patientId) return;

        setIsLoadingHistory(true);
        try {
            const limitParam = visitFilter ? `?limit=${visitFilter}` : '';
            const res = await get(`/followup-report/patient/${patientId}${limitParam}`);
            if (res.status === 200) {
                setHistoryReports(res.data || []);
                setAllTestsMap(buildTestsMap(res.data || []));
            }
        } catch (error) {
            console.error('Error fetching history:', error);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleHistoryClick = () => {
        setShowHistoryModal(true);
        fetchHistory();
    };

    const handleVisitFilterChange = async (e) => {
        const selectedValue = e.target.value;
        const newFilter = selectedValue === 'all' ? null : parseInt(selectedValue);
        setVisitFilter(newFilter);

        if (showHistoryModal && patientId) {
            setIsLoadingHistory(true);
            try {
                const limitParam = newFilter ? `?limit=${newFilter}` : '';
                const res = await get(`/followup-report/patient/${patientId}${limitParam}`);
                if (res.status === 200) {
                    setHistoryReports(res.data || []);
                    setAllTestsMap(buildTestsMap(res.data || []));
                }
            } catch (error) {
                console.error('Error fetching history:', error);
            } finally {
                setIsLoadingHistory(false);
            }
        }
    };


    const handlePrint = () => {
        const patientInfo = {
            name: patientDetails?.name || patientName || 'N/A',
            age: patientAge || 'N/A',
            gender: patientDetails?.gender || 'N/A',
            patientId: patientDetails?.patientId || "---"
        };

        const filename = generatePrintFilename(patientInfo.name);
        printHistory(patientInfo, historyReports, allTestsMap, filename);
    };

    return (
        <div className='follow-up-sheet-section'>
            <div className='follow-up-sheet-header d-flex justify-content-between align-items-center border-bottom pb-2 mb-3'>
                <div className='d-flex align-items-center gap-3'>
                    <button
                        className='btn btn-sm'
                        onClick={handleHistoryClick}
                        style={{
                            backgroundColor: '#6c757d',
                            color: 'white',
                            fontSize: '13px',
                            padding: '3px 16px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        History
                    </button>
                </div>
                <div className='d-flex align-items-center gap-3'>
                    <div>
                        <label className='me-2 mt-1' style={{ fontSize: '13px' }}>Date:</label>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{ fontSize: '13px', padding: '0px 6px' }}
                        />
                    </div>
                    <button
                        className='btn btn-sm save-button ml-4'
                        onClick={handleSave}
                        disabled={isSaving}
                        title='Save'
                        style={{
                            backgroundColor: '#14A67D',
                            color: 'white',
                            padding: '3px 16px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            opacity: isSaving ? 0.7 : 1
                        }}
                    >
                        {isSaving ? 'Saving...' : <p className='py-0 my-0'>Save <small>(Ctrl+S / Cmd+S)</small></p>}
                    </button>
                </div>
            </div>

            {isLoading ? (
                <UniversalLoaderForComponent />
            ) : (
                <div className='follow-up-sheet-content'>
                    <FollowUpTable
                        followupTests={followupTests}
                        testResults={testResults}
                        getTestValue={getTestValue}
                        getBreakdownValue={getBreakdownValue}
                        handleTestValueChange={handleTestValueChange}
                        handleBreakdownValueChange={handleBreakdownValueChange}
                    // handleAutoSave={handleSave}
                    />

                    {/* <div className='notes-section mt-3'>
                        <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Notes:</label>
                        <textarea
                            className="form-control mt-2"
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Enter any additional notes..."
                            style={{ fontSize: '12px' }}
                        />
                    </div> */}
                </div>
            )}

            <HistoryModal
                show={showHistoryModal}
                setShow={setShowHistoryModal}
                patientName={patientName}
                patientDetails={patientDetails}
                historyReports={historyReports}
                allTestsMap={allTestsMap}
                isLoadingHistory={isLoadingHistory}
                visitFilter={visitFilter}
                onVisitFilterChange={handleVisitFilterChange}
                onPrint={handlePrint}
            />
        </div>
    );
};

export default FollowUpSheet;
